import { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DynamicFormField,
  Form,
  FormActions,
  FormError,
  FormField,
  Input,
  useZodForm,
} from '../ui';
import type { FormActionDescriptor } from '../ui';
import {
  CreateSubjectBodySchema,
  createSubject,
  updateSubject,
  subjectsKeys,
  type Subject,
} from '../../lib/api/subjects.gen';
import type { DomainPack } from '../../lib/api/domains.gen';
import { attributeDefaults, attributeFields, attributesSchemaFor } from '../../lib/domainForm';

// The domain-driven Sites create/edit form (P03 §5, §7.1; story 3-1b).
//
// ── G-26 in one expression ────────────────────────────────────────────────
//     CreateSubjectBodySchema.extend({ attributes: attributesSchemaFor(pack…) })
// The envelope is the GENERATED contract schema (`lib/api/subjects.gen.ts`) —
// the same object the backend validates the request with — and only
// `attributes`, whose shape is a data-driven domain pack rather than code, is
// assembled at runtime. That is exactly G-26's "composition is allowed;
// re-declaration is not", with the dynamic regime supplying one sub-schema.
// Nothing here is hand-declared, there is no `useState` form and no ad-hoc
// validation.
//
// ── One form per pack, by remount ─────────────────────────────────────────
// A different pack is a different schema AND a different set of controls, so
// the caller renders `<SubjectForm key={pack.key} pack={pack} …/>`: choosing a
// domain mounts a fresh form rather than mutating a live resolver. The domain
// PICKER lives in the caller (P03 §7.1: it appears only when more than one pack
// is active), which keeps this component a pure "one pack, one record" form.
//
// ── Preserving the user's input ───────────────────────────────────────────
// Every server-side failure P03 §7.1 names — duplicate reference, denied scope,
// an inactive domain, a plain retryable API error — is surfaced WITHOUT
// resetting the form. RHF keeps the typed values; a duplicate reference also
// gets `setError` on the offending field so the message lands next to its
// cause, and the submit button stays live so retry is one click.

export interface SubjectFormProps {
  pack: DomainPack;
  /** Record being edited; omit to create. */
  subject?: Subject;
  onCancel: () => void;
  onSaved: (subject: Subject) => void;
}

interface ApiErrorBody {
  message?: string | undefined;
  reason?: string | undefined;
  field?: string | undefined;
  fields?: Array<{ field: string; message: string }> | undefined;
  featureKey?: string | undefined;
}

/** Read the structured detail the backend's error handler exposes
 * (`err.expose` → response body), falling back to the message. */
function errorBodyOf(error: unknown): { status: number; body: ApiErrorBody } {
  if (isAxiosError(error) && error.response) {
    return { status: error.response.status, body: (error.response.data ?? {}) as ApiErrorBody };
  }
  return { status: 0, body: { message: error instanceof Error ? error.message : undefined } };
}

export function SubjectForm({ pack, subject, onCancel, onSaved }: SubjectFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const isEdit = Boolean(subject);
  const noun = pack.subjectSchema.labels.singular.toLowerCase();

  const fields = useMemo(() => attributeFields(pack.subjectSchema.fields), [pack]);
  const schema = useMemo(
    () => CreateSubjectBodySchema.extend({ attributes: attributesSchemaFor(pack.subjectSchema.fields) }),
    [pack],
  );

  const form = useZodForm(schema, {
    defaultValues: {
      domainKey: pack.key,
      name: subject?.name ?? '',
      referenceCode: subject?.referenceCode ?? '',
      attributes: {
        ...attributeDefaults(pack.subjectSchema.fields),
        ...((subject?.attributes ?? {}) as Record<string, unknown>),
      },
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: { name: string; referenceCode?: string | null; attributes?: unknown }) => {
      const body = {
        name: values.name,
        referenceCode: values.referenceCode ? values.referenceCode : null,
        attributes: (values.attributes ?? {}) as Record<string, unknown>,
      };
      const response = subject
        ? await updateSubject(subject._id, body)
        : await createSubject({ ...body, domainKey: pack.key });
      return response.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: subjectsKeys.all });
      onSaved(saved);
    },
    onError: (error: unknown) => {
      const { status, body } = errorBodyOf(error);
      // Duplicate reference code (409) — the field-level state P03 §7.1 asks
      // for. The typed value stays; the message sits on the control.
      if (body.reason === 'duplicate_reference') {
        form.setError('referenceCode', { type: 'server', message: body.message ?? 'Reference code already in use' });
        setServerError(`${body.message ?? 'That reference code is already in use'} — change it and save again.`);
        return;
      }
      // Server-side attribute validation disagreed with the client's resolver.
      // Placing each message on its own control is what makes it actionable.
      if (body.reason === 'invalid_attributes' && Array.isArray(body.fields)) {
        for (const issue of body.fields) {
          form.setError(`attributes.${issue.field}` as 'attributes', { type: 'server', message: issue.message });
        }
        setServerError('Some fields need attention — your entries are kept below.');
        return;
      }
      if (status === 403) {
        setServerError(
          body.reason === 'domain_inactive'
            ? `${body.message ?? 'That domain is not active for this organization.'} Ask an owner to enable it — nothing you typed has been lost.`
            : `You don't have permission to save this ${noun}. Your entries are kept — ask an owner for the "manage" permission, or cancel.`,
        );
        return;
      }
      setServerError(
        `${body.message ?? 'Something went wrong saving this record.'} Your entries are kept — press Save to retry.`,
      );
    },
  });

  return (
    <Form
      form={form}
      onSubmit={(values) => {
        setServerError(null);
        mutation.mutate(values as { name: string; referenceCode?: string | null; attributes?: unknown });
      }}
      className="mt-4 space-y-4"
    >
      <FormField name="name" label={`${pack.subjectSchema.labels.singular} name`} required>
        {(field) => <Input type="text" placeholder="e.g. 23 Ocean View" {...field} />}
      </FormField>

      <FormField
        name="referenceCode"
        label="Reference code"
        description="Optional. Must be unique across this organization."
      >
        {(field) => <Input type="text" placeholder="Optional" {...field} />}
      </FormField>

      {/* Every remaining control comes from the pack, through the SAME
          renderer the /app/design dynamic-regime gallery uses. */}
      {fields.map((field) => (
        <DynamicFormField key={field.key} field={field} namePrefix="attributes" />
      ))}

      <FormError message={serverError} />

      <FormActions
        className="pt-2"
        actions={
          [
            { id: 'cancel', type: 'button', label: 'Cancel', variant: 'secondary', onClick: onCancel, disabled: mutation.isPending },
            {
              id: 'save',
              type: 'submit',
              label: isEdit ? 'Save changes' : `Save ${noun}`,
              pendingLabel: 'Saving…',
              loading: mutation.isPending,
            },
          ] satisfies FormActionDescriptor[]
        }
      />
    </Form>
  );
}
